import { ArrowRight } from 'lucide-react';
import { useId, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, appCheck } from '../firebaseConfig'; // Adjust path to your firebaseConfig
import { getToken } from 'firebase/app-check';
import { checkSubscriptionRenewal, getBillingGateway } from '../services'; // Import the API function
import CurrencyDropdown from './CurrencyDropdown';
import { SEO } from './SEO';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../utils/navigation';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { getPricingDefaultValue } from '../utils/pricingI18n';
import './componentDarkSurfaceFixes.css';

interface Plan {
  name: string;
  monthlyPrice: string;
  yearlyPrice: string;
  period: string;
  features: string[];
  monthlyPlanId?: string;
  yearlyPlanId?: string;
  popular?: boolean;
}

function StrokeCheck() {
  return (
    <span aria-hidden="true" className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
        <path
          d="M2 7.5 5.5 10.5 12 3.5"
          stroke="#2563eb"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

type MissionBadgeProps = {
  variant?: 'pricing' | 'checkout';
  className?: string;
};

function SealMark({ size = 32, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
      <circle cx="24" cy="24" r="18" stroke="currentColor" strokeWidth="1" opacity="0.2" />
      <path
        d="M16 16v16c0 0 3-2 8-2s8 2 8 2V16c0 0-3-2-8-2s-8 2-8 2z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        fill="currentColor"
        fillOpacity="0.08"
      />
      <path d="M24 14v18" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 30 * Math.PI) / 180;
        const cx = 24 + 20.5 * Math.cos(angle);
        const cy = 24 + 20.5 * Math.sin(angle);
        return <circle key={i} cx={cx} cy={cy} r="0.8" fill="currentColor" opacity="0.25" />;
      })}
    </svg>
  );
}

function MissionBadge({ variant = 'pricing', className = '' }: MissionBadgeProps) {
  const { t } = useTranslation('pricing');
  const tp = (key: string, options?: Record<string, unknown>) =>
    t(key, { defaultValue: getPricingDefaultValue(key), ...(options ?? {}) });

  if (variant === 'checkout') {
    return (
      <div className={`w-full flex items-start gap-3 border border-gray-300 bg-gray-50 px-4 py-3 mb-5 ${className}`}>
        <SealMark size={24} className="text-gray-700 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-600 block mb-0.5">
            {tp('pricing.mission_badge.checkout_label')}
          </span>
          <p className="text-[12px] text-gray-600 leading-snug">
            {tp('pricing.mission_badge.checkout_body')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative mx-auto w-full max-w-2xl overflow-hidden border border-gray-300 bg-white ${className}`}>
      <div className="h-[3px] bg-gradient-to-r from-blue-700 via-blue-600 to-blue-700" />
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 px-4 sm:px-6 py-4 sm:py-5">
        <div className="flex-shrink-0 text-blue-800 self-start sm:self-auto">
          <SealMark size={40} className="sm:hidden" />
          <SealMark size={52} className="hidden sm:block" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="inline-block text-[10px] font-bold tracking-[0.2em] uppercase text-blue-700">
              {tp('pricing.mission_badge.label')}
            </span>
            <span className="hidden sm:inline-block h-px flex-1 bg-gray-200" />
          </div>
          <p className="text-[14px] sm:text-[15px] font-semibold text-gray-900 leading-snug">
            {tp('pricing.mission_badge.title')}
          </p>
          <p className="text-[12px] sm:text-[13px] text-gray-500 leading-relaxed mt-0.5">
            {tp('pricing.mission_badge.body')}
          </p>
        </div>
      </div>
      <div className="h-px bg-gray-200" />
    </div>
  );
}

function PaymentMethodMark({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="h-9 w-14 rounded-lg border border-gray-200 bg-white/90 flex items-center justify-center text-gray-800"
      aria-label={label}
      title={label}
    >
      {children}
    </div>
  );
}

function PaymentMethodsStrip() {
  const { t } = useTranslation('pricing');
  const tp = (key: string, options?: Record<string, unknown>) =>
    t(key, { defaultValue: getPricingDefaultValue(key), ...(options ?? {}) });

  return (
    <div className="mt-6 flex flex-col items-center">
      <div className="flex items-center justify-center gap-2">
        <PaymentMethodMark label="Visa">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="46"
            height="18"
            viewBox="0 0 46 18"
            fill="none"
            className="opacity-80"
            aria-hidden="true"
          >
            <text
              x="23"
              y="14"
              textAnchor="middle"
              fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
              fontSize="14"
              fontWeight="800"
              letterSpacing="2"
              fill="currentColor"
            >
              VISA
            </text>
          </svg>
        </PaymentMethodMark>
        <PaymentMethodMark label="Mastercard">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="20"
            viewBox="0 0 40 20"
            fill="none"
            className="opacity-80"
            aria-hidden="true"
          >
            <circle cx="16" cy="10" r="7.5" fill="currentColor" opacity="0.55" />
            <circle cx="24" cy="10" r="7.5" fill="currentColor" opacity="0.35" />
            <path
              d="M20 3.5c2.6 1.9 4.3 4.4 4.3 6.5S22.6 14.6 20 16.5c-2.6-1.9-4.3-4.4-4.3-6.5S17.4 5.4 20 3.5Z"
              fill="currentColor"
              opacity="0.7"
            />
          </svg>
        </PaymentMethodMark>
        <PaymentMethodMark label="American Express">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="20"
            viewBox="0 0 44 20"
            fill="none"
            className="opacity-80"
            aria-hidden="true"
          >
            <rect x="1" y="1" width="42" height="18" rx="5" stroke="currentColor" strokeWidth="1.5" />
            <text
              x="22"
              y="14"
              textAnchor="middle"
              fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
              fontSize="11"
              fontWeight="800"
              letterSpacing="1.6"
              fill="currentColor"
            >
              AMEX
            </text>
          </svg>
        </PaymentMethodMark>
        <span className="ml-1 text-xs text-gray-500">{tp('pricing.payment_methods.more')}</span>
      </div>
      <div className="mt-2 text-[12px] text-gray-500 text-center">
        {tp('pricing.payment_methods.caption')}
      </div>
    </div>
  );
}

function PricingTrustStrip() {
  const { t } = useTranslation('pricing');
  const tp = (key: string, options?: Record<string, unknown>) =>
    t(key, { defaultValue: getPricingDefaultValue(key), ...(options ?? {}) });

  const items = [
    {
      title: tp('pricing.trust_strip.gdpr_title'),
      subtitle: tp('pricing.trust_strip.gdpr_subtitle'),
      icon: (
        <path
          d="M3 6v4a5 5 0 0010 0V6M5 6V4a3 3 0 016 0v2"
          stroke="currentColor"
          strokeWidth={1.4}
          strokeLinecap="round"
        />
      ),
    },
    {
      title: tp('pricing.trust_strip.cancel_title'),
      subtitle: tp('pricing.trust_strip.cancel_subtitle'),
      icon: (
        <path
          d="M8 1L2 4v5c0 3.3 2.4 6.3 6 7 3.6-.7 6-3.7 6-7V4L8 1z"
          stroke="currentColor"
          strokeWidth={1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: tp('pricing.trust_strip.payment_methods_title'),
      subtitle: tp('pricing.trust_strip.payment_methods_subtitle'),
      icon: (
        <path
          d="M2 4h12v9H2zM2 7h12M5 10h3"
          stroke="currentColor"
          strokeWidth={1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: tp('pricing.trust_strip.token_policy_title'),
      subtitle: tp('pricing.trust_strip.token_policy_subtitle'),
      icon: (
        <path
          d="M2 8a6 6 0 0110-4.5M14 8a6 6 0 01-10 4.5M11 1v3h3M5 15v-3H2"
          stroke="currentColor"
          strokeWidth={1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
  ];

  return (
    <section
      aria-label={tp('pricing.trust_strip.aria_label')}
      className="mt-10 rounded-2xl border border-gray-200 bg-gray-50 px-6 py-6 sm:px-8"
    >
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => (
          <div key={item.title} className="flex items-center gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-950">
              <svg viewBox="0 0 16 16" fill="none" className="h-5 w-5" aria-hidden="true">
                {item.icon}
              </svg>
            </div>
            <div>
              <p className="m-0 text-sm font-medium tracking-[-0.01em] text-gray-950">{item.title}</p>
              <p className="m-0 mt-1 text-sm text-gray-500">{item.subtitle}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MissionPatchBadgeBar({
  className = '',
  variant = 'bar',
}: {
  className?: string;
  variant?: 'bar' | 'pill';
}) {
  const { t } = useTranslation('pricing');
  const tp = (key: string, options?: Record<string, unknown>) =>
    t(key, { defaultValue: getPricingDefaultValue(key), ...(options ?? {}) });
  const [showDetail, setShowDetail] = useState(false);
  const uid = useId().replace(/:/g, '');
  const feltBaseId = `feltBaseMob-${uid}`;
  const goldMobId = `goldMob-${uid}`;
  const isPill = variant === 'pill';

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setShowDetail((v) => !v)}
        aria-expanded={showDetail}
        className={`w-full flex items-center bg-gradient-to-r from-slate-800 to-slate-900 cursor-pointer shadow-lg ${
          isPill ? 'gap-3 rounded-full px-4 py-2' : 'gap-3 rounded-xl px-3 py-2.5'
        }`}
      >
        <svg
          viewBox="0 0 220 260"
          className={`${isPill ? 'w-8 h-10' : 'w-12 h-14'} flex-shrink-0`}
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
        >
          <defs>
            <linearGradient id={feltBaseId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1e3a5f" />
              <stop offset="100%" stopColor="#162d4d" />
            </linearGradient>
            <linearGradient id={goldMobId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#d4a048" />
              <stop offset="50%" stopColor="#f0c860" />
              <stop offset="100%" stopColor="#c89530" />
            </linearGradient>
          </defs>
          <path
            d="M110,12 L198,42 C202,44 205,48 205,52 L205,150 C205,175 185,200 110,245 C35,200 15,175 15,150 L15,52 C15,48 18,44 22,42 Z"
            fill={`url(#${feltBaseId})`}
          />
          <path
            d="M110,16 L195,45 C198,46 201,49 201,52 L201,148 C201,172 182,196 110,240 C38,196 19,172 19,148 L19,52 C19,49 22,46 25,45 Z"
            fill="none"
            stroke={`url(#${goldMobId})`}
            strokeWidth="4"
            strokeDasharray="8 6"
          />
          <g transform="translate(110, 115)" fill="none" stroke="#e8e4d8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M0,-16 C-7,-16 -18,-13 -18,-4 L-18,12 C-18,7 -7,4 0,7" />
            <path d="M0,-16 C7,-16 18,-13 18,-4 L18,12 C18,7 7,4 0,7" />
          </g>
          <text x="110" y="165" textAnchor="middle" fill={`url(#${goldMobId})`} style={{ fontSize: '18px' }}>
            ✦
          </text>
        </svg>
        {isPill ? (
          <div className="min-w-0 text-left flex items-baseline gap-2">
            <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-amber-400/80">
              {tp('pricing.mission_badge.label')}
            </span>
            <span className="text-xs text-white/80 font-medium truncate">{tp('pricing.mission_badge.compact_body')}</span>
          </div>
        ) : (
          <>
            <div className="min-w-0 text-left">
              <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-amber-400/80 block">{tp('pricing.mission_badge.label')}</span>
              <p className="text-xs text-white/80 font-medium">{tp('pricing.mission_badge.compact_body')}</p>
            </div>
            <svg
              className={`w-4 h-4 text-white/40 flex-shrink-0 transition-transform duration-300 ${showDetail ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </>
        )}
      </button>
      {showDetail ? (
        <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <p className="text-xs text-gray-700 leading-relaxed">
            <span className="font-semibold text-gray-900">{tp('pricing.mission_badge.title')}</span>
            <br />
            {tp('pricing.mission_badge.body')}
          </p>
        </div>
      ) : null}
    </div>
  );
}

const plansUSD: Plan[] = [
  {
    name: 'free_plan',
    monthlyPrice: '$0',
    yearlyPrice: '$0',
    period: '/month',
    features: [],
  },
  {
    name: 'Explorer',
    monthlyPrice: '$19.99',
    yearlyPrice: '$199',
    monthlyPlanId: 'plan_SNku4CQRrh0bEl',
    yearlyPlanId: 'plan_SNl0YQdeuosITV',
    period: '/month',
    features: [],
    popular: true,
  },
  {
    name: 'Scholar',
    monthlyPrice: '$39.99',
    yearlyPrice: '$399',
    monthlyPlanId: 'plan_SNkwBT3rmQ6Is9',
    yearlyPlanId: 'plan_SNl217UXbVtV6w',
    period: '/month',
    features: [],
  },
  {
    name: 'Sage',
    monthlyPrice: '$79.99',
    yearlyPrice: '$799',
    monthlyPlanId: 'plan_SNkxSFFL1uLomA',
    yearlyPlanId: 'plan_SNl2ixnX9firod',
    period: '/month',
    features: [],
  },
  {
    name: 'Enlightened',
    monthlyPrice: 'Custom',
    yearlyPrice: 'Custom',
    period: '',
    features: [],
  },
];

const plansINR: Plan[] = [
  {
    name: 'free_plan',
    monthlyPrice: '₹0',
    yearlyPrice: '₹0',
    period: '/month',
    features: [],
  },
  {
    name: 'Explorer',
    monthlyPrice: '₹499',
    yearlyPrice: '₹4,990',
    monthlyPlanId: 'plan_SNku4CQRrh0bEl',
    yearlyPlanId: 'plan_SNl0YQdeuosITV',
    period: '/month',
    features: [],
    popular: true,
  },
  {
    name: 'Scholar',
    monthlyPrice: '₹999',
    yearlyPrice: '₹9,990',
    monthlyPlanId: 'plan_SNkwBT3rmQ6Is9',
    yearlyPlanId: 'plan_SNl217UXbVtV6w',
    period: '/month',
    features: [],
  },
  {
    name: 'Sage',
    monthlyPrice: '₹1,999',
    yearlyPrice: '₹19,990',
    monthlyPlanId: 'plan_SNkxSFFL1uLomA',
    yearlyPlanId: 'plan_SNl2ixnX9firod',
    period: '/month',
    features: [],
  },
  {
    name: 'Enlightened',
    monthlyPrice: 'Custom',
    yearlyPrice: 'Custom',
    period: '',
    features: [],
  },
];

// NOTE: Pricing/quotas here are currently frontend-static (INR vs USD). If these change often,
// consider sourcing prices/plan IDs/quotas/top-up hits from a backend "public billing config"
// endpoint (derived from backend env + /billing/gateway) to prevent frontend/backend drift.
const topUpPlanUSD: Plan = {
  name: 'Top-Up',
  monthlyPrice: '$9.99',
  yearlyPrice: '$9.99',
  period: '/one-time',
  features: [],
};

const topUpPlanINR: Plan = {
  name: 'Top-Up',
  monthlyPrice: '₹249',
  yearlyPrice: '₹249',
  period: '/one-time',
  features: [],
};

export default function PricingSection() {
  const { t } = useTranslation('pricing');
  const tp = (key: string, options?: Record<string, unknown>) =>
    t(key, { defaultValue: getPricingDefaultValue(key), ...(options ?? {}) });
  const isGlobal = typeof window !== 'undefined' && (window as any).__isIndianLocale__ === false;
  const localizedPath = useLocalizedPath();

  const getDynamicTokenFeature = (planName: string) => {
    if (planName === 'Explorer') {
      return currency === 'INR'
        ? isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '43,200', perMonth: '3,600' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '3,600' })
        : isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '150,000', perMonth: '12,500' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '12,500' });
    }

    if (planName === 'Scholar') {
      return currency === 'INR'
        ? isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '86,400', perMonth: '7,200' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '7,200' })
        : isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '300,000', perMonth: '25,000' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '25,000' });
    }

    if (planName === 'Sage') {
      return currency === 'INR'
        ? isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '172,800', perMonth: '14,400' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '14,400' })
        : isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '600,000', perMonth: '50,000' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '50,000' });
    }

    if (planName === 'Top-Up') {
      return tp('pricing.token_labels.add_output_tokens_one_time', {
        count: currency === 'INR' ? '1,800' : '6,000',
      });
    }

    return null;
  };

  const getPlanDisplayPrice = (plan: Plan) =>
    plan.name === 'Enlightened' ? tp('pricing.custom_pricing') : isAnnual ? plan.yearlyPrice : plan.monthlyPrice;

  const getPlanDisplayPeriod = (planName: string) => {
    if (planName === 'Enlightened') {
      return '';
    }

    if (planName === 'Top-Up') {
      return tp('pricing.one_time');
    }

    return isAnnual ? t('pricing.per_year') : t('pricing.per_month');
  };

  const getPlanCopyKey = (planName: string) =>
    planName === 'free_plan' ? 'free' : planName.toLowerCase();

  const getPlanSubtitle = (planName: string) =>
    tp(`pricing.plan_subtitles.${getPlanCopyKey(planName)}`);

  const getPlanDescription = (planName: string) =>
    planName === 'Enlightened'
      ? tp('pricing.plan_descriptions.enlightened')
      : '';
  
  // Helper function to get translated features for each plan
  const getTranslatedFeatures = (planName: string) => {
    const planKey = planName === 'free_plan' ? 'free' : planName.toLowerCase();
    const featuresKey = `${planKey}_features`;
    
    if (planName === 'Top-Up') {
      const translatedTopUp = getDynamicTokenFeature(planName);
      return translatedTopUp ? [translatedTopUp] : [];
    }
    
    const features: string[] = [];
    for (let i = 1; i <= 9; i++) {
      const featureKey = `pricing.${featuresKey}.feature${i}`;
      try {
        const feature = t(featureKey);
        if (feature !== featureKey) { // Only add if translation exists
          features.push(feature);
        }
      } catch (e) {
        break; // Stop if no more features
      }
    }

    const dynamicTokenFeature = getDynamicTokenFeature(planName);
    if (features.length && dynamicTokenFeature) {
      features[0] = dynamicTokenFeature;
    }

    // INR/Razorpay users don't get AI Studio (Weave) access — replace with Day Quality feature
    if (currency === 'INR') {
      return features.map(f =>
        /AI Studio/i.test(f)
          ? t('pricing.explorer_features.feature_day_quality', { defaultValue: 'Day Quality & Muhurta timeline' })
          : f
      );
    }

    return features;
  };

  const getPresentedFeatures = (planName: string) => {
    const features = getTranslatedFeatures(planName);

    if (planName === 'free_plan') {
      return {
        label: tp('pricing.feature_labels.includes'),
        features,
      };
    }

    if (planName === 'Enlightened') {
      return {
        label: tp('pricing.feature_labels.built_for_institutions'),
        features,
      };
    }

    if (['Explorer', 'Scholar', 'Sage'].includes(planName) && features.length > 1) {
      return {
        label: features[1].replace(/:$/, ''),
        features: [features[0], ...features.slice(2)],
      };
    }

    return {
      label: '',
      features,
    };
  };

  const getPlanCtaLabel = (planName: string) =>
    planName === 'Enlightened' ? tp('pricing.talk_to_our_team') :
    planName === 'Explorer' ? t('pricing.upgrade_to_explorer') :
    planName === 'Scholar' ? t('pricing.choose_scholar') :
    planName === 'Sage' ? t('pricing.become_a_sage') : t('pricing.get_started');
  
  const [isAnnual, setIsAnnual] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [currentPlan, setCurrentPlan] = useState<string>('');
  const [remainingHits, setRemainingHits] = useState<string | number>('0');
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isCurrentPlanMonthly, setIsCurrentPlanMonthly] = useState<boolean>(false);
  const navigate = useLocalizedNavigate();
  const [activePopup, setActivePopup] = useState(null);
  const swiftRef = useRef<any>(null);
  const achRef = useRef<any>(null);
  const [currency, setCurrency] = useState<string>('USD');
  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event:any) => {
      if (
        swiftRef.current && !swiftRef.current.contains(event.target) &&
        achRef.current && !achRef.current.contains(event.target) &&
        !event.target.closest('a')
      ) {
        setActivePopup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle currency change from dropdown
  const handleCurrencyChange = (newCurrency: string, _newMultiplier: number) => {
    setCurrency(newCurrency);
  };

  useEffect(() => {
    try {
      const savedCurrency = localStorage.getItem('currency');
      if (savedCurrency) {
        setCurrency(savedCurrency);
      }
    } catch (error) {
      console.error('Error reading currency from localStorage:', error);
      // Defaults are already set in useState
    }
  }, []);

  const plans = currency === 'INR' ? plansINR : plansUSD;
  const topUpPlan = currency === 'INR' ? topUpPlanINR : topUpPlanUSD;
  
  const togglePopup = (popup:any) => {
    setActivePopup(activePopup === popup ? null : popup);
  };
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoggedIn(!!user);
      if (user) {
        try {
          getBillingGateway().catch(() => {});
          // Ensure App Check token is available before Firestore operations
          if (appCheck) {
            try {
              await getToken(appCheck);
              console.log("✅ App Check token verified for PricingSection Firestore operations");
            } catch (appCheckError) {
              console.error("❌ App Check token verification failed in PricingSection:", appCheckError);
              // Continue anyway in development, but log the error
              if (!import.meta.env.DEV) {
                throw appCheckError;
              }
            }
          }
          
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          const userData = userDoc.exists() ? userDoc.data() : {};

          const subscriptionName = userData.subscription_name || '';
          const hits = userData.hits || '0';
          const subscriptionDate = userData.date ? new Date(userData.date) : new Date();
          const period = userData.period || 1; // 1 for monthly, 12 for yearly

          setCurrentPlan(subscriptionName);
          setRemainingHits(hits);
          setIsCurrentPlanMonthly(period === 1);

          const now = new Date();
          const daysDiff = Math.floor((now.getTime() - subscriptionDate.getTime()) / (1000 * 60 * 60 * 24));
          const isPaidPlan = subscriptionName && subscriptionName !== 'free_plan';
          const isPlanExpired = isPaidPlan && ((period === 1 && daysDiff > 30) || (period === 12 && daysDiff > 365));

          setIsExpired(isPlanExpired);

          if (isPlanExpired && subscriptionName !== 'free_plan') {
            const renewalStatus = await checkSubscriptionRenewal(user.uid);
            if (renewalStatus.status === 'updated') {
              const updatedDoc = await getDoc(userDocRef);
              const updatedData = updatedDoc.exists() ? updatedDoc.data() : {};
              setCurrentPlan(updatedData.subscription_name || 'Expired');
              setRemainingHits(updatedData.hits || '0');
              setIsExpired(false);
              setIsCurrentPlanMonthly(updatedData.period === 1);
            }
          }
        } catch (error) {
          console.error('Error fetching subscription data:', error);
          setCurrentPlan('');
          setRemainingHits('0');
          setIsExpired(false);
          setIsCurrentPlanMonthly(false);
        }
      } else {
        setCurrentPlan('');
        setRemainingHits('0');
        setIsExpired(false);
        setIsCurrentPlanMonthly(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handlePlanSelect = (plan: Plan) => {
    // Always route Enlightened to contact page, regardless of login state
    if (plan.name === 'Enlightened') {
      navigate('/contact-us');
      return;
    }
    if (!isLoggedIn) {

      if (plan.name === 'free_plan') {
        navigate('/sign-up')
      }else{
      navigate('/sign-up?pricing=true');
      return;
      }
    }
     else {
      localStorage.setItem('plan', JSON.stringify({ plan, isAnnual }));
      navigate('/plan-details', { state: { plan, isAnnual } });
    }
  };


  const handleSwitchToYearly = (plan: Plan) => {
    if (plan.name === 'Enlightened') {
      navigate('/contact-us');
      return;
    }
    if (!isLoggedIn) {
      navigate('/sign-up?pricing=true');
      return;
    }
    localStorage.setItem('plan', JSON.stringify({ plan, isAnnual: true }));
    navigate('/plan-details', { state: { plan, isAnnual: true, switchToYearly: true } });
  };

  const handleTopUpSelect = () => {
    if (!isLoggedIn) {
      navigate(`/sign-up?pricing=true`);
      return;
    }
    navigate('/plan-details', { state: { plan: topUpPlan, isAnnual: false } });
  };

  // Product schema for SEO using Explorer plan pricing
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Sarvalok Membership",
    "description": "Unlock deeper access to Sarvalok's Dharma knowledge platform with premium features and unlimited queries.",
    "brand": { "@type": "Organization", "name": "Sarvalok" },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "price": "19.99",
      "availability": "https://schema.org/InStock",
      "url": "https://sarvalok.com/pricing"
    }
  };

  return (
    <>
      <SEO
        title={t('seo:pricing.title', { defaultValue: 'Pricing Plans | Sarvalok AI Subscription Options' })}
        description={t('seo:pricing.description', { defaultValue: 'Choose the perfect Sarvalok plan for your needs. Flexible pricing options for individuals, educators, researchers, and organizations seeking authentic cultural knowledge.' })}
        url="/pricing"
        type="product"
        schema={productSchema}
      />
      <div className="sl-pricing-shell bg-white py-10">
      <div
        className="container mx-auto px-6"
        style={{ position: 'relative', zIndex: 0, overflow: 'visible' }}
      >
        {/*
        {isLoggedIn && (
          <div className="text-center mb-8" style={{ position: 'relative', zIndex: 2 }}>
            <p className="text-lg text-gray-700">
              {t('pricing.current_subscription')} <span className="font-semibold">{currentPlan === 'free_plan' ? t('pricing.free') : currentPlan}</span> | 
              Remaining Tokens: <span className="font-semibold">{remainingHits}</span>
              {isExpired && (
                <span className="text-red-500 ml-2"> (Expired - Please renew or upgrade)</span>
              )}
            </p>
          </div>
        )}
        */}
        {/* Keep pricing intro below the fixed nav while scrolling. Header is zIndex 999. */}
        <div className="text-center mb-16" style={{ position: 'relative', zIndex: 0 }}>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('pricing.pricing_title')}</h2>
          <p className="text-gray-600 text-lg">{t('pricing.pricing_description')}</p>
          <div className="flex justify-center items-center gap-4 mt-6">
            <div className="inline-flex border border-gray-300 rounded-full p-1 bg-gray-100">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  !isAnnual ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                {t('pricing.monthly')}
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`flex items-center gap-2 rounded-full py-2 pl-6 pr-2.5 text-sm font-medium transition-colors ${
                  isAnnual ? 'bg-black text-white' : 'text-gray-600'
                }`}
              >
                <span>{t('pricing.annually')}</span>
                <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold leading-none text-white shadow-sm ring-1 ring-white/25">
                  {t('pricing.annual_save_badge', { defaultValue: 'Save 17%' })}
                </span>
              </button>
            </div>
            <div className="hidden" aria-hidden="true">
              <CurrencyDropdown onCurrencyChange={handleCurrencyChange} />
            </div>
          </div>
        </div>
        
        {/* Pricing Bridge Text */}
        <p 
          className="text-center mx-auto mb-4" 
          style={{
            fontFamily: "'Crimson Text', 'Georgia', serif",
            fontSize: '18px',
            fontWeight: 300,
            color: '#64748B',
            fontStyle: 'italic',
            letterSpacing: '0.01em',
            lineHeight: 1.6,
            margin: '20px auto',
            maxWidth: '650px'
          }}
        >
          {t('pricing.pricing_bridge')}
        </p>
        
        {/* Sanskrit Quote */}
        <small 
          className="text-center mx-auto mb-10"
          style={{
            display: 'block',
            marginTop: '4px',
            color: '#666',
            fontStyle: 'italic'
          }}
        >
          <span className="sanskrit-text">
            नास्ति विद्यासमं बलम्
          </span>
          {' — '}
          {t('pricing.sanskrit_translation')}
        </small>

        <div className="mb-10 flex justify-center">
          <MissionPatchBadgeBar variant="pill" className="w-full max-w-md" />
        </div>
        
        {/* Primary tiers: Free, Explorer, Scholar */}
        <div className="grid gap-5 md:grid-cols-3 mb-5">
          {plans.slice(0, 3).map((plan) => {
            const isCurrentUserPlan = isLoggedIn && currentPlan === plan.name;
            const featureGroup = getPresentedFeatures(plan.name);
            const showSwitchToYearly =
              isCurrentUserPlan && isCurrentPlanMonthly && plan.name !== 'free_plan' && plan.name !== 'Enlightened';
            const showPrimaryCta = showSwitchToYearly || (!isCurrentUserPlan && !(plan.name === 'free_plan' && isLoggedIn));
            const buttonLabel = showSwitchToYearly ? t('pricing.switch_to_yearly') : getPlanCtaLabel(plan.name);
            const buttonAction = showSwitchToYearly ? () => handleSwitchToYearly(plan) : () => handlePlanSelect(plan);

            return (
              <div
                key={plan.name}
                className={`sl-pricing-card group relative rounded-2xl border bg-white p-8 text-gray-900 transition duration-200 ease-out hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,11,0.10),0_2px_4px_rgba(10,10,11,0.04)] ${
                  plan.popular
                    ? 'sl-pricing-card-popular border-blue-200 bg-gradient-to-b from-[#fdfdff] to-white shadow-[0_14px_38px_-18px_rgba(37,99,235,0.35)] hover:-translate-y-1'
                    : 'sl-pricing-card-standard border-gray-200'
                } ${isCurrentUserPlan ? 'ring-4 ring-emerald-200' : ''}`}
              >
                {plan.popular && (
                  <span className="absolute -top-4 left-1/2 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-gray-950 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                    {tp('pricing.most_popular')}
                  </span>
                )}
                {isCurrentUserPlan && (
                  <span className="sl-pricing-current-plan absolute right-5 top-5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    {t('pricing.current_plan')}
                  </span>
                )}

                <h3 className="mb-2 text-2xl font-semibold tracking-normal">
                  {plan.name === 'free_plan' ? t('pricing.free_plan') : t(`pricing.${plan.name.toLowerCase()}`)}
                </h3>
                {/* Secondary card copy: keep upright, but lighter/smaller than plan names and prices.
                   Revert to text-base text-gray-500 if this feels too quiet. */}
                <p className="sl-pricing-muted mb-7 text-sm font-normal text-[#8A8A90]">{getPlanSubtitle(plan.name)}</p>

                <div className="mb-7">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold leading-none tracking-normal">
                      {getPlanDisplayPrice(plan)}
                    </span>
                    {getPlanDisplayPeriod(plan.name) ? (
                      <span className="sl-pricing-muted ml-2 text-gray-500">{getPlanDisplayPeriod(plan.name)}</span>
                    ) : null}
                  </div>
                  {plan.name === 'free_plan' ? (
                    <p className="sl-pricing-muted mt-3 text-sm text-gray-500">{tp('pricing.no_credit_card_required')}</p>
                  ) : (
                    <p className="mt-3 flex items-center gap-2 text-sm font-normal text-orange-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-orange-600" />
                      {tp('pricing.launch_offer_limited_period')}
                    </p>
                  )}
                </div>

                {showPrimaryCta ? (
                  <button
                    onClick={buttonAction}
                    className={`mb-8 inline-flex w-full items-center justify-center gap-2 rounded-lg border px-6 py-3 font-medium tracking-[-0.01em] transition ${
                      plan.name === 'Explorer' || showSwitchToYearly
                        ? 'border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700'
                        : 'sl-pricing-button-secondary border-gray-200 bg-white text-gray-950 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {buttonLabel}
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </button>
                ) : null}

                <hr className="sl-pricing-divider mb-4 border-0 border-t-[0.5px] border-slate-200" />
                <p className="sl-pricing-section-label mb-4 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
                  {featureGroup.label}
                </p>
                <ul className="space-y-3">
                  {featureGroup.features.map((feature, index) => (
                    <li
                      key={index}
                      className="sl-pricing-soft flex items-start gap-3 text-[15px] leading-[1.55] text-slate-700"
                    >
                      <StrokeCheck />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Secondary tiers: Sage and Enterprise */}
        <div className="grid gap-5 lg:grid-cols-2">
          {plans.slice(3).map((plan) => {
            const isCurrentUserPlan = isLoggedIn && currentPlan === plan.name;
            const isEnterprise = plan.name === 'Enlightened';
            const featureGroup = getPresentedFeatures(plan.name);
            const showSwitchToYearly =
              isCurrentUserPlan && isCurrentPlanMonthly && plan.name !== 'free_plan' && plan.name !== 'Enlightened';
            const buttonLabel = showSwitchToYearly ? t('pricing.switch_to_yearly') : getPlanCtaLabel(plan.name);
            const buttonAction = showSwitchToYearly ? () => handleSwitchToYearly(plan) : () => handlePlanSelect(plan);

            return (
              <div
                key={plan.name}
                className={`sl-pricing-card group relative overflow-hidden rounded-2xl border p-8 transition duration-200 ease-out hover:-translate-y-0.5 md:grid md:grid-cols-2 md:gap-8 ${
                  isEnterprise
                    ? 'sl-pricing-card-enterprise border-gray-950 bg-gray-950 text-white shadow-[0_20px_40px_-18px_rgba(10,10,11,0.45)] hover:border-gray-800'
                    : 'sl-pricing-card-standard border-gray-200 bg-gradient-to-br from-white to-gray-50 text-gray-900 hover:border-gray-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,11,0.10)]'
                } ${isCurrentUserPlan ? 'ring-4 ring-emerald-200' : ''}`}
              >
                {isEnterprise ? (
                  <div className="pointer-events-none absolute right-0 top-0 h-60 w-60 bg-[radial-gradient(circle_at_top_right,rgba(234,88,12,0.18),transparent_60%)]" />
                ) : null}
                {isCurrentUserPlan && (
                  <span className="sl-pricing-current-plan absolute right-5 top-5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    {t('pricing.current_plan')}
                  </span>
                )}

                <div className="relative mb-8 md:mb-0">
                  {isEnterprise ? (
                    <span className="mb-5 inline-flex rounded-full bg-orange-950/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-400">
                      {tp('pricing.enterprise_label')}
                    </span>
                  ) : null}
                  <h3 className="mb-2 text-2xl font-semibold tracking-normal">
                    {t(`pricing.${plan.name.toLowerCase()}`)}
                  </h3>
                  <p className={`sl-pricing-muted mb-7 text-sm font-normal ${isEnterprise ? 'text-white/65' : 'text-[#8A8A90]'}`}>
                    {isEnterprise ? getPlanDescription(plan.name) : getPlanSubtitle(plan.name)}
                  </p>

                  <div className="mb-7">
                    <div className="flex items-baseline">
                      <span className={`${isEnterprise ? 'text-3xl' : 'text-4xl'} font-bold leading-none tracking-normal`}>
                        {getPlanDisplayPrice(plan)}
                      </span>
                      {getPlanDisplayPeriod(plan.name) ? (
                        <span className={`sl-pricing-muted ml-2 ${isEnterprise ? 'text-white/60' : 'text-gray-500'}`}>
                          {getPlanDisplayPeriod(plan.name)}
                        </span>
                      ) : null}
                    </div>
                    {!isEnterprise ? (
                      <p className="mt-3 flex items-center gap-2 text-sm font-normal text-orange-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-orange-600" />
                        {tp('pricing.launch_offer_limited_period')}
                      </p>
                    ) : null}
                  </div>

                  {/* Pricing CTA tuning: use font-medium + slight negative tracking for a lighter premium feel.
                     Revert to font-semibold and remove tracking-[-0.01em] if this reads too light. */}
                  <button
                    onClick={buttonAction}
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-medium tracking-[-0.01em] transition md:max-w-60 focus:outline-none ${
                      isEnterprise
                        ? 'border-0 bg-orange-600 text-white shadow-none hover:bg-orange-700 focus-visible:ring-2 focus-visible:ring-orange-300/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950'
                        : 'sl-pricing-button-secondary border border-gray-200 bg-white text-gray-950 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {buttonLabel}
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </button>
                </div>

                <div className="relative">
                  <hr className={`sl-pricing-divider mb-4 border-0 border-t-[0.5px] ${isEnterprise ? 'border-white/15' : 'border-slate-200'}`} />
                  <p
                    className={`sl-pricing-section-label mb-4 text-[11px] font-medium uppercase tracking-[0.14em] ${
                      isEnterprise ? 'text-white/60' : 'text-slate-400'
                    }`}
                  >
                    {featureGroup.label}
                  </p>
                  <ul className="space-y-3">
                    {featureGroup.features.map((feature, index) => (
                      <li
                        key={index}
                        className={`sl-pricing-soft flex items-start gap-3 text-[15px] leading-[1.55] ${
                          isEnterprise ? 'text-white/90' : 'text-slate-700'
                        }`}
                      >
                        <StrokeCheck />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {isLoggedIn && (currentPlan === 'Explorer' || currentPlan === 'Scholar' || currentPlan === 'Sage') && (
          <div className="sl-pricing-topup mt-5 rounded-2xl border border-gray-200 bg-white p-8 text-gray-900 transition duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,11,0.10)]">
            <div className="grid gap-6 md:grid-cols-[1fr_1.2fr_auto] md:items-center">
              <div>
                <h3 className="mb-2 text-2xl font-semibold">{t('pricing.top_up')}</h3>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold leading-none">{topUpPlan.monthlyPrice}</span>
                  <span className="sl-pricing-muted ml-2 text-gray-500">{tp('pricing.one_time')}</span>
                </div>
              </div>
              <ul className="space-y-3">
                {getTranslatedFeatures('Top-Up').map((feature, index) => (
                  <li key={index} className="sl-pricing-soft flex items-start gap-3 text-[15px] leading-[1.55] text-slate-700">
                    <StrokeCheck />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleTopUpSelect}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-6 py-3 font-medium tracking-[-0.01em] text-white transition hover:border-blue-700 hover:bg-blue-700 md:w-auto"
              >
                {t('pricing.top_up_now')}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <PricingTrustStrip />

        {/*
        <MissionBadge className="mt-10 mb-6" />
        */}

        <PaymentMethodsStrip />

        {/* Footnotes Section with Bullet Points */}
        <div className="mt-6 text-gray-500 text-xs">
          <ul className="list-disc list-inside space-y-1">
            <li>
              {t('pricing.token_policy.output_tokens_note', {
                defaultValue: 'Tokens listed in plans are output tokens only and may change.',
              })}
            </li>
            {currency === 'INR' ? (
              <li>{tp('pricing.prices_exclude_gst')}</li>
            ) : null}
            <li>{t('pricing.token_policy.point1')}</li>
            <li>{t('pricing.token_policy.point2')}</li>
            <li>
              {currency === 'INR'
                ? tp('pricing.token_policy.point3_inr')
                : t('pricing.token_policy.point3')}
            </li>
            <li>{t('pricing.token_policy.point4')}</li>
            <li>{t('pricing.token_policy.point5')}</li>
            <li>{t('pricing.token_policy.point6')}</li>
            <li>{t('pricing.token_policy.point7')}</li>
            <li>{t('pricing.token_policy.point8')}</li>
            <li>{t('pricing.token_policy.point9')}</li>
          <li>{t('pricing.token_policy.point10')}</li>
            <li>
              {t('pricing.token_policy.fair_use', {
                defaultValue: 'Fair‑use / abuse protection limits may apply to all plans.',
              })}
            </li>
         <li>
          {t('pricing.token_policy.point11')}{' '}
          <a
            href="#"
            className="underline text-blue-600 hover:text-blue-800"
            onClick={(e) => {
              e.preventDefault();
              togglePopup('swift');
            }}
          >
            {t('pricing.token_policy.swift_text')}
          </a>{' / '}
          <a
            href="#"
            className="underline text-blue-600 hover:text-blue-800"
            onClick={(e) => {
              e.preventDefault();
              togglePopup('ach');
            }}
          >
            {t('pricing.token_policy.ach_text')}
          </a>)
        </li>
          </ul>
          {/* SWIFT Popup */}
      {activePopup === 'swift' && (
        <div
          ref={swiftRef}
          className="fixed top-1/4 left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 shadow-lg p-4 rounded-lg w-80 z-10"
        >
          <h3 className="text-lg font-semibold mb-2">
            {t('pricing.swift_popup_title')}
          </h3>
          <p className="text-sm text-gray-600">
            {isGlobal ? (
              <>
                <span className="font-bold">{t('pricing.account_holder_name')}:</span> SARVALOK INC.<br />
                <span className="font-bold">{t('pricing.account_number')}:</span> 212616723460<br />
                <span className="font-bold">{t('pricing.routing_code')}:</span> TRWIUS35XXX<br />
                <span className="font-bold">{t('pricing.routing_type')}:</span> BIC/SWIFT<br />
                <span className="font-bold">{t('pricing.bank_address')}:</span> 108 W 13th St, Wilmington, DE, 19801, United States<br />
                <span className="font-bold">{t('pricing.bank_name')}:</span> Wise US Inc
              </>
            ) : (
              <>
                <span className="font-bold">{t('pricing.account_holder_name')}:</span> DHARMASYA ONLINE (OPC) PRIVATE LIMITED<br />
                <span className="font-bold">{t('pricing.account_number')}:</span> GB33TCCL04140438973316<br />
                <span className="font-bold">{t('pricing.routing_code')}:</span> TCCLGB3L<br />
                <span className="font-bold">{t('pricing.routing_type')}:</span> BIC/SWIFT<br />
                <span className="font-bold">{t('pricing.bank_address')}:</span> 12 Steward Street, The Steward Building, London, E1 6FQ, GB<br />
                <span className="font-bold">{t('pricing.bank_name')}:</span> The Currency Cloud Limited
              </>
            )}
          </p>
          <button
            className="mt-4 bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
            onClick={() => setActivePopup(null)}
          >
            {t('pricing.close')}
          </button>
        </div>
      )}

      {/* ACH Popup */}
      {activePopup === 'ach' && (
        <div
          ref={achRef}
          className="fixed top-1/4 left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 shadow-lg p-4 rounded-lg w-80 z-10"
        >
          <h3 className="text-lg font-semibold mb-2">
            {t('pricing.ach_popup_title')}
          </h3>
          <p className="text-sm text-gray-600">
            {isGlobal ? (
              <>
                <span className="font-bold">{t('pricing.account_holder_name')}:</span> SARVALOK INC.<br />
                <span className="font-bold">{t('pricing.account_number')}:</span> 212616723460<br />
                <span className="font-bold">{t('pricing.routing_code')}:</span> 101019628<br />
                <span className="font-bold">{t('pricing.routing_type')}:</span> ACH / Wire Routing Number<br />
                <span className="font-bold">{t('pricing.bank_address')}:</span> 108 W 13th St, Wilmington, DE, 19801, United States<br />
                <span className="font-bold">{t('pricing.bank_name')}:</span> Wise US Inc
              </>
            ) : (
              <>
                <span className="font-bold">{t('pricing.account_holder_name')}:</span> DHARMASYA ONLINE (OPC) PRIVATE LIMITED<br />
                <span className="font-bold">{t('pricing.account_number')}:</span> 8307162308<br />
                <span className="font-bold">{t('pricing.routing_code')}:</span> 026073150<br />
                <span className="font-bold">{t('pricing.routing_type')}:</span> ACH Routing Number<br />
                <span className="font-bold">{t('pricing.bank_address')}:</span> 810 Seventh Avenue, New York, NY 10019, US<br />
                <span className="font-bold">{t('pricing.bank_name')}:</span> Community Federal Savings Bank
              </>
            )}
          </p>
          <button
            className="mt-4 bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
            onClick={() => setActivePopup(null)}
          >
            {t('pricing.close')}
          </button>
        </div>
      )}
        </div>
      </div>
    </div>
    </>
  );
}
